import React from 'react';

import { equals } from 'ramda';
import { useSuggestionGroupNavigationHooks } from 'src/editor/EditorAlert/SuggestionsAlert/useNavigationHooks';

import { ExpandableRedlineCard, RedlineReasoning } from './ExpandableRedlineCard';
import type { RedlineActionWithContext } from './redline-types';
import { RedlineActionVisualDiff } from './RedlineActionVisualDiff';
import type { SuggestionStatus } from '../../../../chat-types';
import { createAIChatItem } from '../../../helpers/aiChatItem';

interface RedlineActionItemProps {
  actionWithContext: RedlineActionWithContext;
  messageId: string;
  /**
   * The currently selected groupId for navigation highlighting.
   * When this matches the action's groupId, the card is visually highlighted.
   */
  selectedGroupId: string | null;
  /**
   * The status of this action (Pending, Accepted, Rejected, etc.)
   * For now, defaults to Pending until status tracking is implemented.
   */
  status: SuggestionStatus;
  threadId: string;
}

/**
 * Renders a single redline action as an expandable card.
 *
 * Each action represents a specific modification to a paragraph, identified by
 * range markers in the content. The card shows:
 * - Header with truncated reasoning and status chip
 * - Expandable body with full reasoning and diff view
 */
export const RedlineActionItem = React.memo(
  function RedlineActionItem({
    actionWithContext,
    messageId,
    selectedGroupId,
    status,
    threadId,
  }: RedlineActionItemProps) {
    const { action, documentId, locationAnchor } = actionWithContext;
    const isSelected = selectedGroupId === action.groupId;
    const { handleNavigateToRedlineAction } = useSuggestionGroupNavigationHooks();

    const elementId = createAIChatItem({
      groupId: action.groupId,
      messageId,
      threadId,
      type: 'suggestion',
    });

    return (
      <ExpandableRedlineCard
        elementId={elementId}
        isSelected={isSelected}
        status={status}
        title={action.reasoning}
        onCardClick={() =>
          handleNavigateToRedlineAction({ documentId, groupId: action.groupId, locationAnchor })
        }
      >
        <RedlineReasoning reasoning={action.reasoning} />
        <RedlineActionVisualDiff visualDiffSegments={action.visualDiffSegments} />
      </ExpandableRedlineCard>
    );
  },
  // Deep equality check to avoid re-renders during streaming
  (prev, next) => equals(prev, next)
);
